import { NgTemplateOutlet } from '@angular/common';
import { Component, effect, input, contentChild, signal } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';

import { FlyoutMenuComponent } from '../../../components/flyout-menu/flyout-menu.component';
import { FlyoutMenuToggleDirective } from '../../../components/flyout-menu/flyout-menu-toggle.directive';
import { IconComponent } from '../../../components/icon/icon.component';
import { FlyoutMenuOverlayPosition, MenuPanelItem } from '../../../domain/menu.domain';
import { MenuTopGlobalNavigationItemLabelComponent } from './top-navigation-item-label.component';

import type { FlyoutMenuGlobalPosition } from '../../../components/flyout-menu/flyout-menu.component';

@Component({
  selector: 'td-wb-top-navigation-item',
  templateUrl: './top-navigation-item.component.html',
  styleUrls: ['./top-navigation-item.component.scss'],
  imports: [
    FlyoutMenuToggleDirective,
    NgTemplateOutlet,
    IconComponent
  ]
})
export class MenuTopGlobalNavigationItemComponent {
  /**
   * Navigation menu
   */
  readonly item = input<MenuPanelItem>(undefined);
  readonly overlayPosition = input<FlyoutMenuOverlayPosition>(FlyoutMenuOverlayPosition.END_BOTTOM);
  readonly flyoutMenuUserData = input<FlyoutMenuComponent>(undefined);
  readonly positionStrategy = input<'global' | 'flexibleConnected'>('flexibleConnected');
  readonly globalPosition = input<FlyoutMenuGlobalPosition>(undefined);
  readonly customLabel = contentChild(MenuTopGlobalNavigationItemLabelComponent);

  readonly isFlyoutMenuOpened = signal(false);

  constructor() {
    // effect: subscribes to FlyoutMenuComponent open/close outputs — outputToObservable()
    // is required to bridge component outputs to RxJS; onCleanup unsubscribes when
    // flyoutMenuUserData input changes so stale subscriptions are not accumulated.
    effect((onCleanup: (cleanupFn: () => void) => void): void => {
      const flyoutMenu = this.flyoutMenuUserData();
      if (flyoutMenu === undefined) {
        return;
      }

      const openSubscription = outputToObservable(flyoutMenu.open)
        .subscribe((): void => this.onFlyoutMenuOpen());
      const closeSubscription = outputToObservable(flyoutMenu.close)
        .subscribe((): void => this.onFlyoutMenuClose());

      onCleanup((): void => {
        openSubscription.unsubscribe();
        closeSubscription.unsubscribe();
      });
    });
  }

  private onFlyoutMenuOpen(): void {
    this.isFlyoutMenuOpened.set(true);
  }

  private onFlyoutMenuClose(): void {
    this.isFlyoutMenuOpened.set(false);
  }
}
